import React, { useEffect, useState } from 'react';
import { useFormData } from '../../index';
import { useApplication } from '../../../../services/application';
import { API } from '../../../../constants';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Typography from '../../../../components/common/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { useTrans } from '../../../../services/i18n';
import { TextField, Autocomplete } from '../../../../components/common/Inputs';
import { Grid } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { Divider } from '../../../../components/common/Divider';
import { Button } from '../../../../components/common/Buttons';
import { ReactComponent as ArrowBackIcon } from '../../icons/arrow-left.svg';
import * as yup from 'yup';
import { DATE_FORMAT_MM_DD_YYYY } from '../../../../constants/regex';

const useStyles = makeStyles((theme) => ({
  labelDropdowns: {
    fontFamily: 'Lexend',
    marginBottom: '8px',
    color: '#4e5978',
    fontWeight: 500,
  },
  containerCard: {
    padding: '2.125rem 1.5rem 1rem',
  },
  containerFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.3rem 2.3rem 1.87rem 2.3rem',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  backButton: {
    color: '#999999',
    fontSize: 18,
    fontFamily: 'Manrope',
    fontWeight: '500',
    wordWrap: 'break-word',
  },
  backButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  cancelNextButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const URL_REGEX =
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

const generalInformationSchema = yup.object().shape({
  companyName: yup.string().required('Company name is required'),
  businessStartDate: yup
    .string()
    .required('Business start date is required')
    .matches(DATE_FORMAT_MM_DD_YYYY, 'Date format must be MM/DD/YYYY'),
  stateOfIncorporation: yup.string().nullable().required('State of Incorporation is required'),
  phone: yup.string().nullable().required('Business phone is required'),
  profitType: yup.string().nullable().required('Profit Structure is required'),
  corporateUrl: yup
    .string()
    .nullable()
    .notRequired()
    .test('is-valid-url', 'Please enter a valid website URL', (value) => {
      if (!value || value === '') return true;
      return URL_REGEX.test(value);
    }),
});

export default function GeneralInformation({
  nextStep,
  previousStep,
  cancelButtonHandler,
  isEdit,
  onCompletionChange,
  initialValues,
}) {
  const { generalInformationData, setGeneralInformationData } = useFormData();
  const application = useApplication();
  const [states, setStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [profitType, setProfitType] = useState(initialValues.profitType || null);
  const { trans } = useTrans();
  const [phone, setPhone] = useState(generalInformationData?.phone || null);
  const styles = useStyles();
  const [isComplete, setIsComplete] = useState(false);

  const validateField = (fieldName, value) => {
    try {
      yup.reach(generalInformationSchema, fieldName).validateSync(value);
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [fieldName]: err.message }));
    }
  };

  const handleBlur = (fieldName) => () => {
    validateField(fieldName, generalInformationData[fieldName]);
  };

  useEffect(() => {
    getStates();
  }, []);

  const getStates = async () => {
    const payload = await application.call(API.UTILITIES.GET_STATES_BY_COUNTRY, { country: 'US' });
    setStates(payload.map(({ name, isoCode }) => ({ id: isoCode, name })));
  };

  const handleChangePhone = (value) => {
    setPhone(value);
    setGeneralInformationData({
      ...generalInformationData,
      phone: value,
    });

    if (value) {
      setErrors((prev) => ({ ...prev, phone: undefined }));
    }
  };

  useEffect(() => {
    if (onCompletionChange) {
      onCompletionChange(isComplete);
    }
  }, [isComplete, onCompletionChange]);

  const handleProfitChange = (event) => {
    const newValue = event.target.value;

    setProfitType(newValue);
    setGeneralInformationData({
      ...generalInformationData,
      profitType: newValue,
    });
    setErrors((prev) => ({ ...prev, profitType: undefined }));
  };

  const handleNextClick = () => {
    try {
      generalInformationSchema.validateSync(generalInformationData, { abortEarly: false });
      onCompletionChange(true);
      nextStep();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const errorMessages = {};
        error.inner.forEach((err) => {
          if (err.path && !errorMessages[err.path]) {
            errorMessages[err.path] = err.message;
          }
        });
        setErrors(errorMessages);
      }
      onCompletionChange(false);
    }
  };

  return (
    <React.Fragment>
      <Grid container spacing={3} className={styles.containerCard}>
        <div
          style={{
            width: '100%',
            height: '100%',
            paddingLeft: 40,
            paddingRight: 40,
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 24,
            display: 'inline-flex',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              color: 'black',
              fontSize: 21,
              fontFamily: 'Manrope',
              fontWeight: '700',
              wordWrap: 'break-word',
            }}
          >
            General Information
          </div>
          <div style={{ alignSelf: 'stretch', height: 1, background: '#D9D9D9' }} />
        </div>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={`${trans('organization-form-company-name')} *`}
            value={generalInformationData.companyName || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setGeneralInformationData({
                ...generalInformationData,
                companyName: newValue,
              });
              if (newValue.trim()) {
                setErrors((prev) => ({ ...prev, companyName: undefined }));
              }
            }}
            onBlur={handleBlur('companyName')}
            error={!!errors.companyName}
            helperText={errors.companyName}
            placeholder={trans('organization-form-company-name')}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <TextField
            type="text"
            label={`${trans('organization-form-business-start-date')} *`}
            value={generalInformationData.businessStartDate || ''}
            onChange={(e) => {
              const newValue = e.target.value;
              setGeneralInformationData({
                ...generalInformationData,
                businessStartDate: newValue,
              });
              if (newValue.trim()) {
                setErrors((prev) => ({ ...prev, businessStartDate: undefined }));
              }
            }}
            onBlur={handleBlur('businessStartDate')}
            error={!!errors.businessStartDate}
            helperText={errors.businessStartDate}
            placeholder={trans('organization-form-business-start-date')}
            required
          />
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={trans('organization-form-uei-code')}
            value={generalInformationData.ueiCode || ''}
            onChange={(e) => {
              setGeneralInformationData({
                ...generalInformationData,
                ueiCode: e.target.value,
              });
            }}
            placeholder={trans('uei-mayus')}
          />
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={trans('organization-form-cage-code')}
            value={generalInformationData.cageCode || ''}
            onChange={(e) => {
              setGeneralInformationData({
                ...generalInformationData,
                cageCode: e.target.value,
              });
            }}
            placeholder={trans('cage-mayus')}
          />
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={trans('organization-form-dba')}
            value={generalInformationData.dba || ''}
            onChange={(e) => {
              setGeneralInformationData({
                ...generalInformationData,
                dba: e.target.value,
              });
            }}
            placeholder={trans('dba')}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <Autocomplete
            options={states}
            value={states.find((state) => state.name === generalInformationData.stateOfIncorporation) || null}
            label={`${trans('organization-form-state-of-incorporation')} *`}
            placeholder={trans('organization-form-state-of-incorporation')}
            disableClearable
            required
            error={!!errors.stateOfIncorporation}
            onChange={(event) => {
              const selectedOption = event.target.value;
              setGeneralInformationData((prev) => ({
                ...prev,
                stateOfIncorporation: selectedOption ? selectedOption.name : null,
              }));
              if (selectedOption) {
                setErrors((prev) => ({ ...prev, stateOfIncorporation: undefined }));
              }
            }}
            onBlur={() => validateField('stateOfIncorporation', generalInformationData.stateOfIncorporation)}
          />
          {errors.stateOfIncorporation && (
            <Typography variant="body2" style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
              {errors.stateOfIncorporation}
            </Typography>
          )}
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={trans('organization-form-corporate-url')}
            placeholder={trans('organization-form-corporate-url')}
            value={generalInformationData.corporateUrl || ''}
            onChange={(e) => {
              setGeneralInformationData({
                ...generalInformationData,
                corporateUrl: e.target.value,
              });
            }}
            onBlur={handleBlur('corporateUrl')}
            error={!!errors.corporateUrl}
            helperText={errors.corporateUrl}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <Typography weight="500" fontFamily="lexend" color="label" gutter="label" variant="body2">
            {trans('organization-form-phone')} *
          </Typography>
          <PhoneInput
            defaultCountry="US"
            placeholder="Enter phone number"
            value={phone}
            onChange={handleChangePhone}
            onBlur={() => validateField('phone', generalInformationData.phone)}
            style={{
              background: '#f1f3f9',
              padding: '10px',
              borderRadius: '3px',
              fontFamily: 'Lexend',
              border: errors.phone ? '1px solid #f44336' : undefined,
            }}
          />
          {errors.phone && (
            <Typography variant="body2" style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
              {errors.phone}
            </Typography>
          )}
        </Grid>

        <Grid item sm={6} xs={12} px={0}>
          <TextField
            type="text"
            label={trans('organization-form-business-hours')}
            placeholder={trans('organization-form-business-hours')}
            value={generalInformationData.businessHours || ''}
            onChange={(e) => {
              setGeneralInformationData({
                ...generalInformationData,
                businessHours: e.target.value,
              });
            }}
          />
        </Grid>

        <Grid item sm={6} xs={12}>
          <Typography weight="500" fontFamily="lexend" color="label" gutter="label" variant="body2">
            {trans('organization-form-profit-structure')} *
          </Typography>
          <div
            style={{
              width: '100%',
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 0,
              paddingBottom: 0,
              background: 'white',
              borderRadius: 4,
              border: errors.profitType ? '1px solid #f44336' : '0.50px rgba(0, 0, 0, 0.08) solid',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'inline-flex',
            }}
          >
            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={profitType}
              onChange={handleProfitChange}
              row
              style={{ width: '50%', verticalAlign: 'center' }}
            >
              <FormControlLabel
                color="primary"
                style={{
                  color: '#2445A7',
                  width: '100%',
                  marginLeft: '1px',
                }}
                value="profit"
                control={<Radio color="secondary" />}
                label="For Profit"
              />
            </RadioGroup>

            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={profitType}
              onChange={handleProfitChange}
              row
              style={{ width: '50%', verticalAlign: 'center' }}
            >
              <FormControlLabel
                color="primary"
                style={{
                  color: '#2445A7',
                  width: '100%',
                  marginLeft: '1px',
                }}
                value="nonprofit"
                control={<Radio color="secondary" />}
                label="Nonprofit"
              />
            </RadioGroup>

            <RadioGroup
              aria-labelledby="demo-controlled-radio-buttons-group"
              name="controlled-radio-buttons-group"
              value={profitType}
              onChange={handleProfitChange}
              row
              style={{ width: '50%', verticalAlign: 'center' }}
            >
              <FormControlLabel
                color="primary"
                style={{
                  color: '#2445A7',
                  width: '100%',
                  marginLeft: '1px',
                }}
                value="other"
                control={<Radio color="secondary" />}
                label="Other"
              />
            </RadioGroup>
          </div>
          {errors.profitType && (
            <Typography variant="body2" style={{ color: '#f44336', fontSize: 12, marginTop: 4 }}>
              {errors.profitType}
            </Typography>
          )}
        </Grid>
      </Grid>

      <Divider light spacing={1} />

      <Grid container className={styles.containerFooter}>
        <div className={styles.backButtonContainer}>
          <ArrowBackIcon />
          <div className={styles.backButton} onClick={previousStep}>
            {trans('organization-form-back-button')}
          </div>
        </div>
        <div className={styles.cancelNextButtonContainer}>
          <Button variant="contained" color="primary" onClick={cancelButtonHandler}>
            {trans('organization-form-cancel-button')}
          </Button>
          <Button variant="outlined" color="primary" onClick={handleNextClick} style={{ marginLeft: '10px' }}>
            {trans('organization-form-next-button')}
          </Button>
        </div>
      </Grid>
    </React.Fragment>
  );
}
